import React, { useState, useEffect, useCallback } from 'react';
import {
  CalendarDays, ChevronLeft, ChevronRight, Info, Clock, Umbrella,
  AlertCircle, Briefcase, Stethoscope, Sun, ClipboardList, CheckCircle2
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import LeaveSection from '../components/LeaveSection';

const MONTH_NAMES = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];
const WEEKDAYS = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'];

// Styl komórek i legendy wg kategorii dnia.
const TYPE_STYLES = {
  PRACA: {
    label: 'Praca',
    cell: 'bg-white border-gray-200 hover:border-primary-300 hover:shadow-md',
    dot: 'bg-gray-400',
    accent: '',
    badge: 'bg-gray-800 text-white',
    text: 'text-gray-800',
  },
  WOLNE: {
    label: 'Wolne',
    cell: 'bg-gray-50 border-gray-100',
    dot: 'bg-gray-300',
    accent: '',
  },
  URLOP: {
    label: 'Urlop',
    cell: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-md',
    dot: 'bg-blue-500',
    accent: 'border-l-4 border-l-blue-500',
    badge: 'bg-blue-600 text-white',
    text: 'text-blue-900',
    Icon: Umbrella,
  },
  CHOROBOWE: {
    label: 'Chorobowe',
    cell: 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 hover:shadow-md',
    dot: 'bg-amber-500',
    accent: 'border-l-4 border-l-amber-500',
    badge: 'bg-amber-500 text-white',
    text: 'text-amber-900',
    Icon: Stethoscope,
  },
  NIEOBECNOSC: {
    label: 'Inna nieobecność',
    cell: 'bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200 hover:shadow-md',
    dot: 'bg-violet-500',
    accent: 'border-l-4 border-l-violet-500',
    badge: 'bg-violet-600 text-white',
    text: 'text-violet-900',
    Icon: AlertCircle,
  },
};

const formatHours = (h) => {
  if (!h) return '0 h';
  const totalMinutes = Math.round(h * 60);
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return mm === 0 ? `${hh} h` : `${hh}:${String(mm).padStart(2, '0')} h`;
};

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Grafik = () => {
  const today = new Date();
  const todayIso = toIso(today);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [variant, setVariant] = useState('wykonany'); // 'plan' | 'wykonany'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorInfo, setErrorInfo] = useState(null); // { reason, message }

  const fetchGrafik = useCallback(async () => {
    setLoading(true);
    setErrorInfo(null);
    setData(null);
    try {
      const res = await api.get('/kadry/grafik/me', { params: { rok: year, miesiac: month } });
      setData(res.data);
    } catch (err) {
      const resp = err.response?.data;
      setErrorInfo({
        reason: resp?.reason || 'ERROR',
        message: resp?.error || 'Nie udało się pobrać grafiku.',
      });
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchGrafik();
  }, [fetchGrafik]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  };

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;
  const grafikFilled = !!(data && data.grafikFilled);

  // Ułożenie dni w tygodnie (poniedziałek jako pierwszy dzień).
  const buildWeeks = (days) => {
    if (!days || days.length === 0) return [];
    const first = new Date(days[0].date + 'T00:00:00');
    let lead = first.getDay() - 1; // Pn=0 ... Nd=6
    if (lead < 0) lead = 6;
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    days.forEach((d) => cells.push(d));
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  };

  const renderVariantSwitch = () => {
    if (!grafikFilled) return null;
    const btn = (key, label, Icon) => {
      const active = variant === key;
      return (
        <button
          key={key}
          onClick={() => setVariant(key)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
            active ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Icon className="w-4 h-4" /> {label}
        </button>
      );
    };
    return (
      <div className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1 shadow-sm">
        {btn('plan', 'Plan', ClipboardList)}
        {btn('wykonany', 'Wykonany', CheckCircle2)}
      </div>
    );
  };

  const renderHeader = () => (
    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center shadow-md">
          <CalendarDays className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">Mój grafik</h1>
          <p className="text-sm text-gray-500">Grafik pracy i urlopy z systemu kadrowego</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {renderVariantSwitch()}
        <div className="inline-flex items-center bg-white border border-gray-200 rounded-full shadow-sm overflow-hidden">
          <button
            onClick={() => changeMonth(-1)}
            className="px-3 py-2 hover:bg-gray-100 transition-colors text-gray-600"
            aria-label="Poprzedni miesiąc"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="min-w-[170px] text-center font-semibold text-gray-800 select-none">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button
            onClick={() => changeMonth(1)}
            className="px-3 py-2 hover:bg-gray-100 transition-colors text-gray-600"
            aria-label="Następny miesiąc"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <button
          onClick={goToday}
          disabled={isCurrentMonth}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
            isCurrentMonth
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-default'
              : 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700 shadow-sm'
          }`}
        >
          Dziś
        </button>
      </div>
    </div>
  );

  const renderGrafikBanner = () => {
    if (!data) return null;
    if (grafikFilled) {
      return (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          Grafik z kadr: ułożony ({variant === 'plan' ? 'widok planowany' : 'widok wykonany'}).
          Plan {data.planDays} dni, wykonanie {data.wykonanyDays} dni.
        </div>
      );
    }
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
          <Info className="w-4 h-4" />
        </div>
        <div className="text-sm">
          <p className="font-medium text-amber-900">
            Grafik na {MONTH_NAMES[month - 1].toLowerCase()} {year} nie został jeszcze ułożony w kadrach.
          </p>
          <p className="text-amber-800/80 mt-0.5">
            Poniżej pokazujemy normę czasu pracy z ewidencji (bez godzin zmian). Godziny i symbole
            pojawią się, gdy kadry uzupełnią grafik.
          </p>
        </div>
      </div>
    );
  };

  const SummaryCard = ({ icon: Icon, label, value, sub, tone }) => {
    const tones = {
      blue: 'bg-primary-50 text-primary-700',
      sky: 'bg-blue-100 text-blue-700',
      amber: 'bg-amber-100 text-amber-700',
    };
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${tones[tone]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold tracking-wider text-gray-500 uppercase">{label}</div>
          <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
          {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!data) return null;
    const absenceDays = (data.absences || []).reduce((sum, a) => sum + a.days, 0);
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard icon={Clock} label="Godziny pracy" value={formatHours(data.workHours)} tone="blue" />
        <SummaryCard
          icon={Umbrella}
          label="Urlop w miesiącu"
          value={`${data.vacationDays} ${data.vacationDays === 1 ? 'dzień' : 'dni'}`}
          sub={data.vacationHours ? formatHours(data.vacationHours) : null}
          tone="sky"
        />
        <SummaryCard
          icon={AlertCircle}
          label="Nieobecności"
          value={`${absenceDays} ${absenceDays === 1 ? 'dzień' : 'dni'}`}
          sub={data.absences && data.absences.length > 0 ? `${data.absences.length} ${data.absences.length === 1 ? 'typ' : 'typy'}` : 'brak'}
          tone="amber"
        />
      </div>
    );
  };

  const renderLegend = () => (
    <div className="flex flex-wrap gap-2">
      {Object.entries(TYPE_STYLES).map(([key, s]) => (
        <span
          key={key}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 text-xs font-medium text-gray-700 shadow-sm"
        >
          <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
          {s.label}
        </span>
      ))}
    </div>
  );

  // Zawartość komórki gdy grafik z kadr (ZCP) jest ułożony - wybrany wariant plan/wykonany.
  const renderShiftContent = (shift, style) => {
    if (shift.free) {
      return (
        <span className="text-[11px] text-gray-400 flex items-center gap-1">
          <Sun className="w-3.5 h-3.5" /> wolne
        </span>
      );
    }
    const isWork = shift.category === 'PRACA';
    return (
      <>
        <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-bold tracking-wide ${style.badge}`}>
          {shift.symbol}
        </span>
        {isWork ? (
          <>
            <span className="text-sm font-semibold text-gray-800 tabular-nums mt-1">
              {shift.from}–{shift.to}
            </span>
            {shift.name && <span className="text-[10px] text-gray-400 mt-0.5 truncate max-w-full">{shift.name}</span>}
          </>
        ) : (
          <span className={`text-[11px] font-semibold leading-tight mt-1 ${style.text}`}>{shift.name}</span>
        )}
      </>
    );
  };

  // Zawartość komórki gdy brak grafiku z kadr - norma z ewidencji OBC (jak dotychczas).
  const renderNormContent = (day, style) => {
    const isAbsence = ['URLOP', 'CHOROBOWE', 'NIEOBECNOSC'].includes(day.type);
    if (day.type === 'PRACA') {
      return (
        <>
          <span className="text-lg font-semibold text-gray-800 tabular-nums">{day.symbol}</span>
          <span className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
            <Briefcase className="w-3 h-3" /> norma
          </span>
        </>
      );
    }
    if (day.type === 'WOLNE') {
      return (
        <span className="text-[11px] text-gray-400 flex items-center gap-1">
          <Sun className="w-3.5 h-3.5" /> wolne
        </span>
      );
    }
    if (isAbsence) {
      return (
        <>
          <span className={`text-[11px] font-semibold leading-tight ${style.text}`}>{day.typeName}</span>
          {day.hours > 0 && (
            <span className={`text-[10px] mt-0.5 opacity-70 ${style.text}`}>{day.symbol} · {formatHours(day.hours)}</span>
          )}
        </>
      );
    }
    return null;
  };

  const renderDayCell = (day, di) => {
    if (!day) return <div key={di} className="min-h-[104px]" />;

    const shift = grafikFilled ? day[variant] : null;
    const useShift = !!shift;
    const category = useShift ? shift.category : day.type;
    const style = TYPE_STYLES[category] || TYPE_STYLES.PRACA;
    const dayNum = new Date(day.date + 'T00:00:00').getDate();
    const isToday = day.date === todayIso;
    const isWeekend = day.weekend;
    const isAbsence = ['URLOP', 'CHOROBOWE', 'NIEOBECNOSC'].includes(category);
    const AbsenceIcon = style.Icon;
    const baseTitle = useShift
      ? (shift.free ? 'Dzień wolny' : `${shift.symbol}${shift.name ? ' – ' + shift.name : ''}${shift.from ? ` (${shift.from}–${shift.to})` : ''}`)
      : (day.typeName || style.label);
    const title = useShift && shift.note ? `${baseTitle}\n${shift.note}` : baseTitle;

    return (
      <div
        key={di}
        title={title}
        className={`relative min-h-[104px] rounded-xl border p-2.5 flex flex-col transition-all duration-150 ${style.cell} ${style.accent || ''} ${
          isToday ? 'ring-2 ring-primary-500 ring-offset-1' : ''
        }`}
      >
        <div className="flex items-start justify-between">
          <span
            className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold ${
              isToday
                ? 'bg-primary-600 text-white shadow'
                : isWeekend
                ? 'text-red-400'
                : category === 'WOLNE'
                ? 'text-gray-400'
                : 'text-gray-700'
            }`}
          >
            {dayNum}
          </span>
          {isAbsence && AbsenceIcon && (
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${style.badge}`}>
              <AbsenceIcon className="w-3.5 h-3.5" />
            </span>
          )}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center mt-1 min-w-0">
          {useShift ? renderShiftContent(shift, style) : renderNormContent(day, style)}
        </div>
      </div>
    );
  };

  const renderGrid = () => {
    const weeks = buildWeeks(data.days);
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 overflow-x-auto">
        <div className="min-w-[700px]">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {WEEKDAYS.map((w, i) => (
              <div
                key={w}
                className={`text-center text-[11px] font-semibold tracking-wider uppercase py-2 rounded-lg ${
                  i >= 5 ? 'text-red-500 bg-red-50' : 'text-gray-500 bg-gray-50'
                }`}
              >
                {w}
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-2">
                {week.map((day, di) => renderDayCell(day, di))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderAbsencesTable = () => {
    if (!data || !data.absences || data.absences.length === 0) return null;
    const groupToType = (g) => {
      const gg = (g || '').toUpperCase();
      if (gg === 'URL') return 'URLOP';
      if (gg === 'CHR') return 'CHOROBOWE';
      return 'NIEOBECNOSC';
    };
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-800">Nieobecności w miesiącu</h2>
        </div>
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-5 py-2 text-left text-[11px] font-semibold tracking-wider text-gray-500 uppercase">Typ</th>
              <th className="px-5 py-2 text-right text-[11px] font-semibold tracking-wider text-gray-500 uppercase">Dni</th>
              <th className="px-5 py-2 text-right text-[11px] font-semibold tracking-wider text-gray-500 uppercase">Godziny</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.absences.map((a, i) => {
              const s = TYPE_STYLES[groupToType(a.typeGroup)];
              return (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3 text-sm text-gray-800 flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                    {a.typeName}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-800 text-right font-medium tabular-nums">{a.days}</td>
                  <td className="px-5 py-3 text-sm text-gray-600 text-right tabular-nums">{formatHours(a.hours)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderError = () => {
    const notLinked = errorInfo.reason === 'NOT_LINKED' || errorInfo.reason === 'NOT_MATCHED';
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5" />
          </div>
          <div>
            <p className="text-gray-900 font-medium">{errorInfo.message}</p>
            {notLinked && (
              <p className="text-sm text-gray-500 mt-1">
                Aby zobaczyć swój grafik, administrator musi ustawić w Twoim koncie
                e-mail kadrowy zgodny z systemem kadrowym.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {renderHeader()}
      <LeaveSection />
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : errorInfo ? (
        renderError()
      ) : data ? (
        <>
          {renderGrafikBanner()}
          {renderSummary()}
          {renderLegend()}
          {renderGrid()}
          {renderAbsencesTable()}
        </>
      ) : null}
    </div>
  );
};

export default Grafik;
